import _, { isEqual } from "lodash";
import { getFromDataPath, reduceDataPath } from "../../../utility";
import { ArsTableBody, ArsTableGroup, ArsTableSubGroup } from "../interface";
import { isArsTableSubGroup } from "../typeguards/isArsTableSubGroup";

/**
 * Resolves the set of groups that should receive new operations when a drop action is performed.
 *
 * For root-level targets (`/columns/N`) the target group itself is the sole entry.
 * For non-root targets the parent is inspected: if all sibling sub-groups currently
 * carry identical sub-group trees (ignoring `id` fields) every sibling is included so
 * that the table stays consistent across column groups; otherwise only the directly
 * targeted group is updated.
 *
 * @param bodyData - Current ArsTable body data
 * @param targetDataPath - JSON-Pointer path to the group that received the drop
 * @param isT - Type guard for the table's generic label type `T`
 * @returns A mapping of data path → group for each group to be updated, or `null`
 *          when the target or its parent cannot be resolved
 */
export const arst_checkSiblingSubgroups = <T>(
  bodyData: ArsTableBody<T>,
  targetDataPath: string,
  isT: (v: unknown) => v is T,
): Record<string, ArsTableSubGroup<T>> | null => {
  const targetGroups: Record<string, ArsTableSubGroup<T>> = {};
  let pathParts = targetDataPath.split("/").filter(Boolean);
  if (pathParts.length >= 4 && pathParts[pathParts.length - 2] === "dataValues") {
    targetDataPath = reduceDataPath(targetDataPath, 2);
    pathParts = targetDataPath.split("/").filter(Boolean);
  }

  if (pathParts.length === 2 && ["columns", "rows"].includes(pathParts[0])) {
    const thisGroup = getFromDataPath(targetDataPath, bodyData, (obj) =>
      isArsTableSubGroup(obj, isT),
    );
    if (!thisGroup) return null;
    targetGroups[targetDataPath] = thisGroup;
  } else {
    // Cannot add only dataValues target path, move up to group level
    const parentDataPath = reduceDataPath(
      targetDataPath,
      pathParts[pathParts.length - 2] === "dataValues" ? 4 : 2,
    );
    const parentGroup = getFromDataPath(parentDataPath, bodyData, (obj) =>
      isArsTableSubGroup(obj, isT),
    );
    if (!parentGroup) return null;

    let referenceSubGroups: Omit<ArsTableGroup<T>, "id">[] | undefined;
    if (
      parentGroup.subGroups?.every((sg, ix) => {
        if (!isArsTableSubGroup(sg, isT)) return false;
        if (ix === 0) {
          referenceSubGroups = sg.subGroups?.map((sub) => _.omit(sub, "id"));
          return true;
        }
        return isEqual(
          sg.subGroups?.map((sub) => _.omit(sub, "id")),
          referenceSubGroups,
        );
      })
    ) {
      (parentGroup.subGroups as ArsTableSubGroup<T>[]).forEach((sg, ix) => {
        targetGroups[`${parentDataPath}/subGroups/${ix}`] = sg;
      });
    } else {
      const thisGroup = getFromDataPath(targetDataPath, bodyData, (obj) =>
        isArsTableSubGroup(obj, isT),
      );
      if (!thisGroup) return null;
      targetGroups[targetDataPath] = thisGroup;
    }
  }

  return targetGroups;
};
